import {
  Autocomplete,
  Avatar,
  Box,
  CircularProgress,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import debounce from "lodash/debounce";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import Highlight from "../../../../Highlight";
import {
  getEligibleWorkers,
  getWorkersStaffingStatus,
} from "../../../store/full-time-shifts-slice";
import {
  FullTimeShiftsState,
  Worker,
} from "../../../store/full-time-shifts-type";
import { NON_EFFECTIVE_STATUS_CODES } from "../constants";
import TruncatedText from "../truncated-text";
import { WorkerItem } from "./worker-item";

const useWorkerStatusBatchFetcher = (shiftId: string, dispatch: any) => {
  const queueRef = useRef<string[]>([]);

  // Debounced function to process the queue
  const processQueue = useMemo(
    () =>
      debounce(() => {
        if (queueRef.current.length === 0) return;

        const uniqueIds = [...new Set(queueRef.current)];
        dispatch(getWorkersStaffingStatus({ workerIds: uniqueIds, shiftId }));

        // Clear queue
        queueRef.current = [];
      }, 150), // Wait 150ms to collect render batch
    [dispatch, shiftId]
  );

  const addToQueue = useCallback(
    (workerId: string) => {
      queueRef.current.push(workerId);
      processQueue();
    },
    [processQueue]
  );

  return addToQueue;
};

const WorkerOption = ({
                        props,
                        option,
                        shiftId,
                        currentJobId,
                        currentWorkcenterId,
                        allWorkcenters,
                        jobs,
                        addToQueue,
                      }: {
  props: React.HTMLAttributes<HTMLLIElement>;
  option: Worker;
  shiftId: string;
  currentJobId: number;
  currentWorkcenterId: number;
  allWorkcenters: any[];
  jobs: any[];
  addToQueue: (id: string) => void;
}) => {
  const { t } = useTranslation();
  // Select specific staffing status for this worker
  const staffingData = useSelector((state: any) =>
    state.fullTimeShifts.getWorkersStaffingStatus.workers.find(
      (w: any) => w.workerId === option.id
    )
  );

  const staffingStatusShiftId = useSelector((state: any) =>
    state.fullTimeShifts.getWorkersStaffingStatus.shiftId
  );

  const isLoading = !staffingData || staffingStatusShiftId !== shiftId;

  useEffect(() => {
    // If we don't have data, or data is from a different shift, fetch it
    if (!staffingData || staffingStatusShiftId !== shiftId) {
      addToQueue(option.id);
    }
  }, [option.id, staffingData, staffingStatusShiftId, shiftId, addToQueue]);

  // Helper to determine Gap Color
  const getGapColor = (status: string | null | undefined) => {
    switch (status) {
      case "OS": return { bg: "#E8F5E9", color: "#1B5E20", label: t("full-time-shifts_overstaffed") };
      case "US": return { bg: "#F9DEDC", color: "#B71C1C", label: t("full-time-shifts_understaffed") };
      default: return { bg: "#F9DEDC", color: "#B71C1C", label: t("full-time-shifts_at-capacity") }; // At Capacity or null
    }
  };

  // Logic to show Job/Workcenter if different
  const showLocationInfo = useMemo(() => {
    if (!staffingData) return false;

    // Check if Job is different OR Workcenter is different
    const isJobDiff = staffingData.primaryJobId !== currentJobId;
    const isWcDiff = staffingData.workcenterId !== currentWorkcenterId;

    return isJobDiff || isWcDiff;
  }, [staffingData, currentJobId, currentWorkcenterId]);

  const locationText = useMemo(() => {
    if (!showLocationInfo || !staffingData) return null;

    const jobName = jobs.find((j) => j.id === staffingData.primaryJobId)?.name ?? t("full-time-shifts_unknown-job");
    const wcName = allWorkcenters.find((wc) => wc.id === staffingData.workcenterId)?.name ?? t("full-time-shifts_unknown-work-center");

    return `${jobName} • ${wcName}`;
  }, [showLocationInfo, staffingData, jobs, allWorkcenters, t]);

  const gapStyle = getGapColor(staffingData?.staffingStatus);

  return (
    <li {...props} key={option.id} style={{ display: "block" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "8px 12px",
          width: "100%",
        }}
      >
        {/* Left Side: Avatar + Text */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flex: 1, overflow: "hidden" }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: "14px",
              backgroundColor: "#e0e0e0",
              border: "1px solid rgba(0,0,0,0.14)",
              color: "rgba(0,0,0,0.87)",
              flexShrink: 0,
            }}
          >
            {option.initials}
          </Avatar>
          <Box sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Typography sx={{ fontSize: "14px", color: "rgba(0,0,0,0.87)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {option.name}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "rgba(0,0,0,0.6)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {option.info}
            </Typography>

            {/* Show Location Info if different */}
            {isLoading ? (
              <Skeleton variant="text" width={120} height={16} sx={{ mt: 0.5 }} />
            ) : (
              showLocationInfo && (
                <Typography sx={{ fontSize: "11px", color: "rgba(0,0,0,0.54)", marginTop: "2px", fontStyle: "italic" }}>
                  {locationText}
                </Typography>
              )
            )}
          </Box>
        </Box>

        {/* Right Side: Gap Badge */}
        <Box sx={{ flexShrink: 0 }}>
          {isLoading ? (
            <Skeleton variant="circular" width={24} height={24} />
          ) : (
            <Tooltip title={`${gapStyle.label}`} placement="left">
              <Box
                sx={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  backgroundColor: gapStyle.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontSize: "12px", fontWeight: 500, color: gapStyle.color }}>
                  {staffingData?.staffingStatus === "US" ? "-" : ""}{staffingData?.gap ?? 0}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    </li>
  );
};

interface JobRowProps {
  jobId: number;
  shiftId: string;
  jobName: string;
  workers: Worker[];
  moveWorker: (worker: Worker, toJobId: number) => void;
  demand: number;
  jobStatus: "OS" | "US" | "";
  value: number;
  search: string;
  setPinnedWorker: React.Dispatch<React.SetStateAction<Worker | null>>;
  pinnedWorker: Worker | null;
  handlePinWorker: (worker: Worker, e: React.MouseEvent) => void;
  hasPermissionToReassignWorkers: boolean;
  hasPermissionToChangeStatus: boolean;
  getAvailableStatusOptions: (
    currentCode: string,
    isMoved: boolean,
    originalStatus: string
  ) => {
    id: number;
    label: string;
    action: (worker: Worker) => void;
  }[];
  workerToDrop: Worker | null;
  setWorkerToDrop: React.Dispatch<React.SetStateAction<Worker | null>>;
  setWorkerToJobId: React.Dispatch<React.SetStateAction<number | null>>;
  lastMovedWorker: Worker | null;
  setLastMovedWorker: React.Dispatch<React.SetStateAction<Worker | null>>;
  dropdownContainerRef: React.RefObject<HTMLDivElement>;
  workcenterId?: number;
}

const statusChipStyles: Record<
  string,
  {
    backgroundColor: string;
    color: string;
  }
> = {
  COF: {
    backgroundColor: "#F9DEDC",
    color: "#rgba(0,0,0,0.87)",
  },
  RDU: {
    backgroundColor: "#F9DEDC",
    color: "#rgba(0,0,0,0.87)",
  },
  TRA: {
    backgroundColor: "#F1DFFA",
    color: "#rgba(0,0,0,0.87)",
  },
  SCH: {
    backgroundColor: "#EEEEEE",
    color: "#rgba(0,0,0,0.87)",
  },
  RAS: {
    backgroundColor: "#FFF3E0",
    color: "#rgba(0,0,0,0.87)",
  },
};

export const JobRow = forwardRef<HTMLDivElement, JobRowProps>(
  (
    {
      jobId,
      shiftId,
      jobName,
      workers,
      moveWorker,
      demand,
      jobStatus,
      value,
      search,
      setPinnedWorker,
      pinnedWorker,
      handlePinWorker,
      hasPermissionToReassignWorkers,
      hasPermissionToChangeStatus,
      getAvailableStatusOptions,
      workerToDrop,
      setWorkerToDrop,
      setWorkerToJobId,
      lastMovedWorker,
      setLastMovedWorker,
      dropdownContainerRef,
      workcenterId,
    }: JobRowProps,
    ref
  ) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const activeWorker = workerToDrop ?? pinnedWorker;
    const [isHovered, setIsHovered] = useState(false);

    const allWorkcenters = useSelector(
      (state: any) => state.fullTimeShifts.filterData?.allWorkcenters
    );
    const jobs = useSelector(
      (state: any) => state.fullTimeShifts.filterData?.jobs
    );

    const addToQueue = useWorkerStatusBatchFetcher(shiftId, dispatch);

    const [isAddingWorker, setIsAddingWorker] = useState(false);
    // Removed redundant local state for options and loading

    const eligibleWorkers = useSelector(
      (state: any) => state.fullTimeShifts.getEligibleWorkers?.eligibleWorkers
    );

    const getEligibleWorkersStatus = useSelector(
      (state: any) => state.fullTimeShifts.getEligibleWorkers?.status
    );

    const filteredEligibleWorkers = useMemo(() => {
        let filteredWorkers: Worker[] = [];
        if (eligibleWorkers) {
            // Remove already existing workers by id
            const existingWorkersIds = workers.map(worker => worker.id);
            filteredWorkers = eligibleWorkers.filter(
              (worker: { workerId: string }) =>
                !existingWorkersIds.includes(worker.workerId)
            );
        }
        return filteredWorkers;
    },[eligibleWorkers, workers])

    // Derived state from Redux store
    const isLoading = getEligibleWorkersStatus === "pending";

    const handleMouseDown = (e: React.MouseEvent, worker: Worker) => {
      handlePinWorker(worker, e);
    };

    const handleMouseUp = (e: React.MouseEvent, worker: Worker) => {
      handleClickDrop();
    };
    const handleClickDrop = () => {
      if (
        activeWorker?.id &&
        jobId !== undefined &&
        activeWorker?.jobId !== jobId
      ) {
        if (
          !activeWorker?.eligibleJobIds.includes(jobId) &&
          activeWorker?.originalJobId !== jobId
        ) {
          setWorkerToJobId(jobId);
          setPinnedWorker(null);
        } else {
          setLastMovedWorker(activeWorker);
          moveWorker(activeWorker, jobId);
          setPinnedWorker(null);
          setWorkerToDrop(null);
          setWorkerToJobId(null);
        }
      } else {
        setPinnedWorker(null);
        setWorkerToDrop(null);
        setWorkerToJobId(null);
      }
    };

    // --- ADDED: Click handler for the "Understaffed" box ---
    const handleAddWorkerClick = () => {
      if (isAddingWorker || isLoading || !shiftId) return; // Don't re-trigger

      setIsAddingWorker(true);
      // Dispatch action to fetch workers
      dispatch(getEligibleWorkers({ jobIds: [jobId], shiftId }));
    };

    // --- ADDED: Selection handler for the Autocomplete ---
    const handleWorkerSelect = (
      event: React.SyntheticEvent,
      selectedWorker: Worker | null
    ) => {
      if (selectedWorker) {
        moveWorker(selectedWorker, jobId);
        // For now, just close the autocomplete
        setIsAddingWorker(false);
      }
    };

    // --- ADDED: Custom renderer for Autocomplete options ---
    const renderWorkerOption = (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: Worker
    ) => {
      return (
        <WorkerOption
          key={option.id}
          props={props}
          option={option}
          shiftId={shiftId}
          currentJobId={jobId}
          currentWorkcenterId={workcenterId ?? 0}
          allWorkcenters={allWorkcenters || []}
          jobs={jobs || []}
          addToQueue={addToQueue}
        />
      );
    };

    const eligibleJobIds = activeWorker?.eligibleJobIds;
    const isBeingDragged = Boolean(activeWorker || pinnedWorker);
    const isHoveredAndBeingDragged = isHovered && isBeingDragged;
    const isJobEligible =
      !eligibleJobIds ||
      eligibleJobIds.includes(jobId) ||
      activeWorker?.originalJobId === jobId;

    const backgroundColor = isHoveredAndBeingDragged
      ? isJobEligible
        ? "rgba(0,0,0,0.02)" // normal hover bg for eligible job
        : "rgba(0,0,0,0.04)"
      : "transparent";

    const staffingDisplay = useMemo(() => {
      // ... (staffingDisplay logic unchanged)
      if (value === 0) {
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: "8px",
            }}
          >
            <Box
              sx={{
                backgroundColor: "#E8F5E9",
                borderRadius: "12px",
                width: "24px",
                height: "24px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  fontFamily: "Roboto",
                  color: "rgba(0,0,0,0.87)",
                }}
              >
                {value}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "rgba(0,0,0,0.87)",
              }}
            >
              <Highlight value={t("full-time-shifts_at-capacity")} searchText={search} />
            </Typography>
          </Box>
        );
      }

      const label = jobStatus === "OS" ? t("full-time-shifts_overstaffed") : t("full-time-shifts_understaffed");
      const color = jobStatus === "OS" ? "#FFE5B9" : "#F9DEDC";

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "8px",
          }}
        >
          <Box
            sx={{
              backgroundColor: color,
              borderRadius: "12px",
              width: "24px",
              height: "24px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontFamily: "Roboto",
                color: "rgba(0,0,0,0.87)",
              }}
            >
              {value}
            </Typography>
          </Box>
          <Typography
            sx={{ fontSize: "14px", fontWeight: 400, color: "rgba(0,0,0,0.87)" }}
          >
            <Highlight value={label} searchText={search} />
          </Typography>
        </Box>
      );
    }, [value, jobStatus, search, t]);

    return (
      <Box
        ref={ref}
        sx={{
          width: "100%",
        }}
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsHovered(false)}
      >
        <Box
          onClick={handleClickDrop}
          onMouseUp={handleClickDrop}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            width: "100%",
            flexDirection: "row",
            borderBottom: "0.8px solid #ccc",
            backgroundColor,
            transition: "background-color 0.2s ease",
            minHeight: "90.5px",
          }}
        >
          {/* --- Job Info Box (Left Side) - Unchanged --- */}
          <Box
            sx={{
              display: "flex",
              width: "45%",
              position: "sticky",
              top: -6,
              zIndex: 1,
              alignSelf: "flex-start",
              minHeight: "58px",
              marginTop: "16px",
            }}
          >
            {/* ... (Job Name, Demand, Staffing display - all unchanged) ... */}
            <Box
              sx={{
                width: `${(20 / 45) * 100}%`,
                paddingLeft: "14px",
                minHeight: "58px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                paddingTop: "16px",
                paddingBottom: "16px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                }}
              >
                {isBeingDragged && jobId === activeWorker?.originalJobId && (
                  <Box
                    sx={{
                      backgroundColor: "#F2F5F7",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      marginLeft: "-9px",
                      marginTop: "-28px",
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Roboto",
                        fontSize: "12px",
                        fontWeight: 400,
                        color: "rgba(0,0,0,0.87)",
                        maxWidth: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("full-time-shifts_primary-job")}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    maxWidth: "216px",
                  }}
                >
                  <TruncatedText text={jobName} searchedTerm={search} />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                width: `${(10 / 45) * 100}%`,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "58px",
                paddingTop: "16px",
                paddingBottom: "16px",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "rgba(0,0,0,0.87)",
                }}
              >
                {demand}
              </Typography>
            </Box>
            <Box
              sx={{
                width: `${(15 / 45) * 100}%`,
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                minHeight: "58px",
                paddingTop: "16px",
                paddingBottom: "16px",
              }}
            >
              {staffingDisplay}
            </Box>
          </Box>

          {/* --- Worker List Box (Right Side) --- */}
          <Box
            sx={{
              display: "flex",
              width: "55%",
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                justifyContent: "center",
                paddingTop: "16px",
                paddingBottom: "16px",
              }}
            >
              {/* --- Worker Items --- */}
              {workers.map((worker, index) => (
                // Removed redundant React.Fragment
                <WorkerItem
                  key={worker.id}
                  worker={worker}
                  search={search}
                  pinnedWorker={pinnedWorker}
                  hasPermissionToReassignWorkers={
                    hasPermissionToReassignWorkers
                    && !worker.isPatternChange
                  }
                  hasPermissionToChangeStatus={
                    hasPermissionToChangeStatus
                    && !worker.isPatternChange
                  }
                  fadeOut={
                    !(
                      (isBeingDragged && worker.id === activeWorker?.id) ||
                      !isBeingDragged
                    )
                  }
                  index={index}
                  totalWorkersCount={workers.length}
                  getAvailableStatusOptions={getAvailableStatusOptions}
                  statusChipStyles={statusChipStyles}
                  onMouseUp={handleMouseUp}
                  onMouseDown={handleMouseDown}
                  lastMovedWorker={lastMovedWorker?.id === worker.id}
                />
              ))}

              {/* --- MODIFIED: Demand Box (Drop Target / Add Worker) --- */}
              {((isBeingDragged && jobId !== activeWorker?.jobId) ||
                  !isBeingDragged) &&
                !(!isBeingDragged && jobStatus !== "US") && (
                  <Box
                    key={`worker-demand-${jobId}`}
                    // ADDED: onClick handler
                    onClick={
                      !isBeingDragged &&
                      jobStatus === "US" &&
                      !isAddingWorker &&
                      !isLoading &&
                      hasPermissionToReassignWorkers
                        ? handleAddWorkerClick
                        : undefined
                    }
                    sx={{
                      width: "55%",
                      maxWidth: "320px",
                      minHeight: "58px", // Use minHeight
                      height: "auto", // Allow box to grow
                      border: "1px dashed rgba(0,0,0,0.6)",
                      borderRadius: "4px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: workers.length !== 0 ? "4px" : "0px",
                      backgroundColor: isJobEligible
                        ? "rgba(0,0,0,0.01)"
                        : "rgba(0,0,0,0.04)",
                      // ADDED: pointer cursor when clickable
                      cursor:
                        !isBeingDragged &&
                        jobStatus === "US" &&
                        !isAddingWorker &&
                        !isLoading &&
                        hasPermissionToReassignWorkers
                          ? "pointer"
                          : "default",
                      padding: isAddingWorker ? "8px 12px" : undefined, // Add padding
                      boxSizing: "border-box", // Ensure padding doesn't break layout
                    }}
                  >
                    {isBeingDragged ? (
                        // --- DRAGGING LOGIC (Unchanged) ---
                        <Typography
                          sx={{
                            fontFamily: "Roboto",
                            fontSize: "12px",
                            cursor: "default",
                            color: "rgba(0,0,0,0.6)",
                            userSelect: "none",
                          }}
                        >
                          {isJobEligible
                            ? activeWorker?.originalJobId === jobId
                              ? t("full-time-shifts_drop-to-schedule")
                              : t("full-time-shifts_drop-to-reassign")
                            : t("full-time-shifts_worker-not-qualified")}
                        </Typography>
                      ) : // --- NOT DRAGGING LOGIC ---
                      isAddingWorker ? (
                        // --- NEW: Autocomplete UI ---
                        isLoading ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Autocomplete
                            sx={{ width: "100%" }} // Fill the container
                            open // Always open when in 'isAddingWorker' state
                            autoHighlight
                            options={filteredEligibleWorkers ?? []}
                            loading={isLoading} // Use derived state
                            getOptionLabel={(option) =>
                              `${option.name} (${option.info})`
                            }
                            renderOption={renderWorkerOption}
                            onChange={handleWorkerSelect}
                            onClose={() => setIsAddingWorker(false)} // Close on blur
                            slotProps={{
                                popper: {
                                    container: dropdownContainerRef.current,
                                    sx: {
                                      width: "400px !important", // Wide enough for Name + Location + Badge
                                    },
                                },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={t("full-time-shifts_add-eligible-worker")}
                                variant="standard"
                                autoFocus
                                InputProps={{
                                  ...params.InputProps,
                                  disableUnderline: true,
                                  endAdornment: (
                                    <>
                                      {isLoading ? (
                                        <CircularProgress
                                          color="inherit"
                                          size={20}
                                        />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        )
                      ) : (
                        // --- "UNDERSTAFFED" TEXT LOGIC (Original) ---
                        <Typography
                          sx={{
                            fontFamily: "Roboto",
                            fontSize: "12px",
                            cursor: hasPermissionToReassignWorkers ? "pointer" : "default",
                            color: "rgba(0,0,0,0.6)",
                            userSelect: "none",
                          }}
                        >
                          {(() => {
                            const understaffedCount =
                              demand -
                              workers?.filter(
                                (worker) =>
                                  !NON_EFFECTIVE_STATUS_CODES.includes(
                                    worker.status?.code
                                  )
                              )?.length;
                            return understaffedCount > 1
                              ? t("full-time-shifts_understaffed-count-other", { count: understaffedCount })
                              : t("full-time-shifts_understaffed-count-one", { count: understaffedCount });
                          })()}
                        </Typography>
                      )}
                  </Box>
                )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  });

JobRow.displayName = "JobRow";